import { FlattenEdges } from "@pathwright/utils"
import { useMemo } from "react"
import {
  InboxGroupConnectionFragment,
  InboxPendingReviewTarget,
  UserFragment,
  useInboxGroupConnectionsQuery
} from "../../api/generated"
import { flattenEdges, usePaginator } from "../../utils/apollo"

export type InboxGroupNode = {
  id: number | null
  type: InboxPendingReviewTarget
  meta: { title: string; subtitle?: string }
  users: {
    staff: UserFragment[]
    members: UserFragment[]
  }
  data: any
}

const parseGroupNodes = (
  nodes: FlattenEdges<InboxGroupConnectionFragment>[]
) => {
  return nodes.map((n) => {
    const node: Partial<InboxGroupNode> = {
      type: n.mentor_group
        ? InboxPendingReviewTarget.Group
        : InboxPendingReviewTarget.Path,
      meta: { title: "", subtitle: "" },
      users: {
        staff: [],
        members: []
      },
      data: { ...(n.mentor_group || n.cohort) }
    }
    if (n.cohort) {
      node.id = n.cohort.id
      node.meta!.title = n.cohort.resource!.name!
      node.meta!.subtitle = n.cohort.name
      node.users = {
        staff:
          n.cohort.registrations
            ?.filter((registration) => registration.role > 5)
            ?.map((registration) => registration.user!) || [],
        members:
          n.cohort.registrations
            ?.filter((registration) => registration.role <= 5)
            ?.map((registration) => registration.user!) || []
      }
    } else if (n.mentor_group) {
      node.id = n.mentor_group.id
      node.meta!.title = n.mentor_group.name!
      node.users = {
        staff:
          n.mentor_group.memberships
            ?.filter((membership) => membership.role > 5)
            .map((membership) => membership.user) || [],
        members:
          n.mentor_group.memberships
            ?.filter((membership) => membership.role <= 5)
            .map((membership) => membership.user) || []
      }
    }

    return node as InboxGroupNode
  })
}

const useInboxItems = ({ search = null } = {}) => {
  const query = useInboxGroupConnectionsQuery({
    variables: {
      search
    }
  })
  const { data, previousData, loading, error } = query

  const items = useMemo(() => {
    // Fallback to previousData to avoid flicker when loading more.
    const d = data || previousData
    if (d) {
      return parseGroupNodes(flattenEdges(d.inboxGroupConnections))
    }
    return []
  }, [data])

  const { loadMore, hasMore, loadingMore } = usePaginator({
    data: query, // hacky
    path: "inboxGroupConnections"
  })

  return { items, loading, loadMore, hasMore, loadingMore, error }
}

export default useInboxItems
